/* */
import React, { useState, useCallback, Fragment } from 'react';
import { Alert } from 'reactstrap';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import Loading from 'components/Loading';
import ErrorMessage from 'components/ErrorMessage';

import SubmitPurchaseDialog from './Dialog/SubmitPurchaseDialog';
import { CommonIcons } from 'components/Icons';
import NewDesign from 'components/NewDesign';
import { PayViaEnum, SIZE_ICON } from 'consts/data';
import SelectField from 'customField/SelectField';
import useToggleDialog from 'hooks/useToggleDialog';
import { Field, Formik } from 'formik';
import InputField from 'customField/InputField';
import { suppressNonNumericInput } from 'helpers';
import * as Yup from 'yup';
import { NUMBER_FIELD, REQUIRED_FIELD } from 'consts/message';
import { localeNumber } from 'helpers/string';
import getUser from 'redux/selector/getUser';

const MIN_PACK_CUSTOM = 2000;
const Package = ({ pack, onClick, disabled, isCustomable, onSubmitCustomPrice }) => {
  //! State
  const validationSchema = Yup.object().shape({
    price: Yup.number()
      .typeError(NUMBER_FIELD('Package'))
      .min(MIN_PACK_CUSTOM, `Package must be >= $${localeNumber(MIN_PACK_CUSTOM)}`)
      .required(REQUIRED_FIELD('Package')),
  });

  //! Render
  const renderPack = () => {
    if (isCustomable) {
      return (
        <div style={{ marginBottom: 20 }}>
          <Field
            component={InputField}
            placeholder='Custom package'
            name='price'
            addOnText='$'
            addOnType='prepend'
            onKeyDown={suppressNonNumericInput}
          />

          <NewDesign.Typography variant='subtitle2' component='p' className='mt-1'>
            Package value should be {'>='} {MIN_PACK_CUSTOM}
          </NewDesign.Typography>
        </div>
      );
    }

    return (
      <NewDesign.Typography className='package__price'>
        $ {(pack || 0).toLocaleString('en-US')}
      </NewDesign.Typography>
    );
  };

  return (
    <Formik
      initialValues={{ price: '' }}
      validationSchema={validationSchema}
      onSubmit={onSubmitCustomPrice}
    >
      {({ handleSubmit }) => (
        <div className='component:Package package'>
          {/* <span className="package__title">{title}</span> */}
          {/* <span className="package__sub-title">{subTitle}</span> */}
          {renderPack()}
          <hr />
          <NewDesign.Typography className='package__btn'>
            <NewDesign.Button
              type='button'
              className='btn btn-primary'
              onClick={isCustomable ? handleSubmit : onClick}
              disabled={disabled}
            >
              <CommonIcons.ShoppingCart /> Charge
            </NewDesign.Button>
          </NewDesign.Typography>
        </div>
      )}
    </Formik>
  );
};

const ListPackage = ({ isFetchingCard, onClickToRedirectPaymentMethod }) => {
  const userInfo = getUser();
  const {
    settings: { enableCreditCard },
    payMethodsAvailable,
  } = userInfo;
  const [payVia, setPayVia] = useState(
    enableCreditCard ? PayViaEnum.CreditCard : PayViaEnum.BankTransfer
  );

  const [packageSelected, setPackageSelected] = useState({});

  const [openPurchaseDialog, togglePurchaseDialog, shouldRenderPurchaseDialog] = useToggleDialog();

  const paymentReducer = useSelector((state) => state.paymentReducer);
  const {
    isFetchingListPackage,
    isFetchedListPackage,
    listPackage,
    errorFetchPackages,
    paymentSelected,
  } = paymentReducer;

  const onClickPackage = useCallback(
    (id, el) => {
      return () => {
        setPackageSelected({ id, amount: el });
        togglePurchaseDialog();
      };
    },
    [packageSelected, togglePurchaseDialog, paymentSelected]
  );

  const onChangePayVia = (value) => {
    setPayVia(value);
  };

  const onSubmitCustomPack = (values) => {
    const { price } = values;

    const lastIndexPackage = (listPackage || []).length + 1;
    onClickPackage(lastIndexPackage, Number(price))();
  };

  const renderData = () => {
    if (isFetchingListPackage) return <Loading />;

    if (errorFetchPackages) return <ErrorMessage />;

    if (isFetchedListPackage) {
      if (listPackage.length === 0) {
        return 'No package has available!';
      }

      const isDisabledCharge = payVia === PayViaEnum.CreditCard && isEmpty(paymentSelected);

      return (
        <Fragment>
          {!isFetchingCard && payVia === PayViaEnum.CreditCard && isEmpty(paymentSelected) && (
            <Alert color='primary'>
              <CommonIcons.Tooltip size={SIZE_ICON} /> You don't have any payment method,{' '}
              <NewDesign.Typography
                onClick={onClickToRedirectPaymentMethod}
                className='tw-underline tw-cursor-pointer hover:tw-opacity-60'
              >
                please come and add a payment method
              </NewDesign.Typography>
            </Alert>
          )}

          <div className='checkout-form__list-package'>
            {listPackage.map((el, index) => (
              <Package
                key={el}
                pack={el}
                onClick={onClickPackage(index + 1, el)}
                disabled={isDisabledCharge}
              />
            ))}

            {payVia === PayViaEnum.BankTransfer && (
              <Package
                isCustomable
                onSubmitCustomPrice={onSubmitCustomPack}
                disabled={isDisabledCharge}
              />
            )}
          </div>
        </Fragment>
      );
    }
  };

  return (
    <div>
      {shouldRenderPurchaseDialog && (
        <SubmitPurchaseDialog
          isOpen={openPurchaseDialog}
          toggle={togglePurchaseDialog}
          packageSelected={packageSelected}
          isBankTransfer={payVia === PayViaEnum.BankTransfer}
        />
      )}
      <div className='package-title'>
        <NewDesign.Typography component='h4'>Packages</NewDesign.Typography>

        <div>
          <SelectField
            options={payMethodsAvailable.filter((el) => el.value !== PayViaEnum.Referral)}
            label='Pay via:'
            value={payVia}
            afterOnChange={onChangePayVia}
          />
        </div>
      </div>

      {renderData()}
    </div>
  );
};

export default React.memo(ListPackage);
